import { useMemo, useCallback } from 'react';
import { useExpandedMode } from '../../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { FilterComponentType, FilterItem } from '../../common/filters/filters-renderer/types';
import { FiltersRenderer } from '../../common/filters/filters-renderer/FiltersRenderer';
import { activeFilterOptions } from '../activity-state/helpers';
import { DocumentationFiltersTypesMap } from '../selected-object/filters/filterTypes';
import { useSelector } from 'react-redux';
import { selectMetadataTypesList } from '../../../reducers/documentation/metadataReducer';
import { NO_NAMESPACE_VALUE } from './useMetadataList';
import { useDocumentationFiltersContext } from '../selected-object/filters/DocumentationFiltersContext';
import { checkFiltersHaveValuesCb } from '../selected-object/filters/utils';

// Cache for computed namespaces to avoid recomputation
const namespaceCache = new Map<string, Array<string | null | undefined>>();

const getOptimizedNamespaces = (metadataData: any[] | undefined, cacheKey: string) => {
  if (!metadataData?.length) return [];

  if (namespaceCache.has(cacheKey)) {
    return namespaceCache.get(cacheKey)!;
  }

  const namespaceSet = new Set<string | null | undefined>();
  for (const item of metadataData) {
    namespaceSet.add(item.namespace);
  }

  const uniqueNamespaces = Array.from(namespaceSet).sort((a, b) => {
    const aNormalized = a || '';
    const bNormalized = b || '';

    if (aNormalized === bNormalized) return 0;
    if (aNormalized === '') return -1; // Empty namespaces first
    if (bNormalized === '') return 1;

    return aNormalized.localeCompare(bNormalized);
  });

  if (namespaceCache.size > 10) {
    const firstKey = namespaceCache.keys().next().value;
    if (firstKey) {
      namespaceCache.delete(firstKey);
    }
  }
  namespaceCache.set(cacheKey, uniqueNamespaces);

  return uniqueNamespaces;
};

export const MetadataFilters = ({
  forceCompact,
  crmOrgId,
  activeTab,
}: {
  forceCompact?: boolean;
  crmOrgId: string;
  activeTab: string;
}) => {
  const metadataTypesList = useSelector(selectMetadataTypesList(crmOrgId));
  const metadataData = useMemo(
    () => metadataTypesList?.[activeTab],
    [metadataTypesList, activeTab],
  );
  const { isExpandedMode } = useExpandedMode();
  const { filterValues, setFilterValue, clearAllFilterValues } = useDocumentationFiltersContext();

  const namespaces = useMemo(() => {
    const cacheKey = `${crmOrgId}-${activeTab}-${metadataData?.length || 0}`;
    return getOptimizedNamespaces(metadataData, cacheKey);
  }, [metadataData, crmOrgId, activeTab]);

  const namespaceFilterItems = useMemo(() => {
    if (!namespaces.length) return [];

    return namespaces.map((namespace) => ({
      label: namespace ? namespace : '(Blank)',
      value: namespace ? namespace : NO_NAMESPACE_VALUE,
    }));
  }, [namespaces]);

  const activeStateFilter = useMemo(() => {
    const filter: FilterItem = {
      type: FilterComponentType.SINGLE_SELECT,
      items: activeFilterOptions,
      label: 'Show',
    };
    return filter;
  }, []);

  const filters = useMemo(() => {
    return {
      [DocumentationFiltersTypesMap.namespace]: {
        type: FilterComponentType.MULTI_SELECT,
        items: namespaceFilterItems,
        label: 'Namespace',
      },
      [DocumentationFiltersTypesMap.activeState]: activeStateFilter,
    };
  }, [namespaceFilterItems, activeStateFilter]);

  const handleFilterChange = useCallback(
    (filterKey: string, values: string[]) => {
      setFilterValue(filterKey, values);
    },
    [setFilterValue],
  );

  const withPanelWrapper = forceCompact || !isExpandedMode;

  return (
    <FiltersRenderer
      filters={filters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      onClearAll={clearAllFilterValues}
      withPanelWrapper={withPanelWrapper}
      gap={2}
      checkFiltersHaveValuesCb={checkFiltersHaveValuesCb}
    />
  );
};
